import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { apiClient } from '@/api/apiClient';
import { useAuthStore } from '@/auth/authStore';

/** What the sign-in page and the app bar show for the organisation: the name, a logo, colours, a welcome line. */
export interface PublicBranding {
  organisationName: string;
  logoDataUrl: string | null;
  primaryColour: string | null;
  secondaryColour: string | null;
  loginMessage: string | null;
  organisationSlug: string;
  /** Addresses of the sign-in background and the favicon, when the organisation has set them (docs §273). */
  loginBackgroundUrl?: string | null;
  faviconUrl?: string | null;
}

const baseURL = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '';

/**
 * The branding before anyone is signed in comes from the host name the browser is at — the
 * organisation whose access domain this is, else the platform's. Once signed in, the person's own
 * organisation's branding is read through the authenticated settings, so an administrator's change
 * shows at once. Both are cached for the session; a failure falls back to the product's own look.
 */
export function useBranding(): PublicBranding | null {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const { data } = useQuery({
    queryKey: ['branding', isAuthenticated ? 'mine' : 'public', typeof window !== 'undefined' ? window.location.host : ''],
    queryFn: async () => {
      if (isAuthenticated) {
        // Any signed-in person may read their organisation's branding; the settings endpoint it once used needs the settings right, which a driver or an ordinary user does not hold.
        const r = await apiClient.get<{ organizationName: string; logoDataUrl: string | null; primaryColour: string | null; secondaryColour: string | null; loginMessage: string | null;
          loginBackgroundUrl?: string | null; faviconUrl?: string | null }>('/api/v1/branding/me');
        return { organisationName: r.data.organizationName, logoDataUrl: r.data.logoDataUrl, primaryColour: r.data.primaryColour, secondaryColour: r.data.secondaryColour, loginMessage: r.data.loginMessage,
          organisationSlug: '', loginBackgroundUrl: r.data.loginBackgroundUrl ?? null, faviconUrl: r.data.faviconUrl ?? null } as PublicBranding;
      }
      const r = await axios.get<PublicBranding>(`${baseURL}/api/v1/branding`, { params: { host: window.location.host } });
      return r.data;
    },
    staleTime: 10 * 60 * 1000,
    retry: false,
  });
  return data ?? null;
}

/** An address the API serves, made absolute when the API is elsewhere. */
export const brandingAsset = (url: string | null | undefined) => (url ? (url.startsWith('http') ? url : `${baseURL}${url}`) : null);
